/**
 * CEDRA Badminton Shopify Theme JavaScript
 * Handles Hero Slider, Featured Carousel, AJAX Cart Drawer, Search Overlay, Mobile Menu, FAQ Accordions, and Filters
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeroSlider();
  initFeaturedCarousel();
  initCartDrawer();
  initSearchDrawer();
  initMobileMenu();
  initFaqAccordions();
  initProductPage();
  initCollectionFilters();
});

/* ==========================================================================
   HERO BANNER SLIDER ENGINE (TRUE INFINITE LOOP)
   ========================================================================== */
function initHeroSlider() {
  const sliderSection = document.querySelector('.hero-slider-section');
  if (!sliderSection) return;

  const track = sliderSection.querySelector('.hero-slider-track');
  if (!track) return;

  const originalSlides = Array.from(track.children);
  const dotsContainer = sliderSection.querySelector('.hero-slider-dots');
  const prevBtn = sliderSection.querySelector('.hero-slider-arrow.prev');
  const nextBtn = sliderSection.querySelector('.hero-slider-arrow.next');

  if (originalSlides.length <= 1) return;

  const realCount = originalSlides.length;

  // Clone first and last slides for continuous infinite looping
  const firstClone = originalSlides[0].cloneNode(true);
  const lastClone = originalSlides[realCount - 1].cloneNode(true);

  firstClone.classList.add('clone-slide');
  lastClone.classList.add('clone-slide');

  track.appendChild(firstClone);
  track.insertBefore(lastClone, originalSlides[0]);

  const allSlides = Array.from(track.children);
  const totalSlides = allSlides.length;

  let currentIndex = 1; // Start at first real slide (index 1)
  let isTransitioning = false;
  let autoplayInterval = null;

  const isAutoplay = sliderSection.getAttribute('data-autoplay') === 'true';
  const autoplaySpeed = parseInt(sliderSection.getAttribute('data-autoplay-speed')) || 5000;

  // Set initial position
  track.style.transition = 'none';
  track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;

  // Generate / sync dots
  if (dotsContainer) {
    dotsContainer.innerHTML = '';
    for (let i = 0; i < realCount; i++) {
      const dot = document.createElement('span');
      dot.className = `dot ${i === 0 ? 'active' : ''}`;
      dot.setAttribute('data-slide', i);
      dotsContainer.appendChild(dot);
    }
  }

  const dots = sliderSection.querySelectorAll('.hero-slider-dots .dot');

  function updateDots() {
    let realIndex = (currentIndex - 1 + realCount) % realCount;
    dots.forEach((dot, idx) => {
      dot.classList.toggle('active', idx === realIndex);
    });
    allSlides.forEach((slide, idx) => {
      slide.classList.toggle('active', idx === currentIndex);
    });
  }

  function goToSlide(index, animate = true) {
    if (isTransitioning && animate) return;
    if (animate) {
      isTransitioning = true;
      track.style.transition = 'transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)';
    } else {
      track.style.transition = 'none';
    }

    currentIndex = index;
    track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;
    updateDots();
  }

  track.addEventListener('transitionend', () => {
    isTransitioning = false;
    // Seamless jump if at clone boundary
    if (currentIndex === 0) {
      track.style.transition = 'none';
      currentIndex = realCount;
      track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;
      updateDots();
    } else if (currentIndex === totalSlides - 1) {
      track.style.transition = 'none';
      currentIndex = 1;
      track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;
      updateDots();
    }
  });

  function nextSlide() {
    if (isTransitioning) return;
    goToSlide(currentIndex + 1);
  }

  function prevSlide() {
    if (isTransitioning) return;
    goToSlide(currentIndex - 1);
  }

  function startAutoplay() {
    if (!isAutoplay) return;
    stopAutoplay();
    autoplayInterval = setInterval(() => {
      nextSlide();
    }, autoplaySpeed);
  }

  function stopAutoplay() {
    if (autoplayInterval) clearInterval(autoplayInterval);
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      nextSlide();
      startAutoplay();
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      prevSlide();
      startAutoplay();
    });
  }

  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      goToSlide(idx + 1);
      startAutoplay();
    });
  });

  // Touch & Drag Swipe Support
  let startX = 0;
  let currentX = 0;
  let isDragging = false;

  sliderSection.addEventListener('touchstart', (e) => {
    startX = e.touches[0].clientX;
    currentX = startX;
    isDragging = true;
    stopAutoplay();
  }, { passive: true });

  sliderSection.addEventListener('touchmove', (e) => {
    if (!isDragging) return;
    currentX = e.touches[0].clientX;
  }, { passive: true });

  sliderSection.addEventListener('touchend', () => {
    if (!isDragging) return;
    isDragging = false;
    const diffX = startX - currentX;
    if (Math.abs(diffX) > 40) {
      if (diffX > 0) nextSlide();
      else prevSlide();
    }
    startAutoplay();
  }, { passive: true });

  // Keyboard Navigation
  document.addEventListener('keydown', (e) => {
    const rect = sliderSection.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      if (e.key === 'ArrowRight') {
        nextSlide();
        startAutoplay();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
        startAutoplay();
      }
    }
  });

  sliderSection.addEventListener('mouseenter', stopAutoplay);
  sliderSection.addEventListener('mouseleave', startAutoplay);

  updateDots();
  startAutoplay();
}

/* ==========================================================================
   FEATURED PRODUCTS CAROUSEL (TRUE INFINITE LOOP)
   ========================================================================== */
function initFeaturedCarousel() {
  const sections = document.querySelectorAll('.products-section');
  sections.forEach(sec => {
    const trackWrapper = sec.querySelector('.product-carousel-track-wrapper');
    const track = sec.querySelector('.product-carousel-track');
    const prevBtn = sec.querySelector('.arrow-btn.feat-prev, .arrow-btn:first-child');
    const nextBtn = sec.querySelector('.arrow-btn.feat-next, .arrow-btn:last-child');

    if (!trackWrapper || !track) return;

    const originalCards = Array.from(track.children);
    if (originalCards.length <= 1) return;

    const realCount = originalCards.length;

    // Clone cards at head and tail for seamless infinite scrolling
    originalCards.forEach(card => {
      const cloneEnd = card.cloneNode(true);
      cloneEnd.classList.add('clone-card');
      track.appendChild(cloneEnd);
    });
    originalCards.forEach(card => {
      const cloneStart = card.cloneNode(true);
      cloneStart.classList.add('clone-card');
      track.insertBefore(cloneStart, track.firstChild);
    });

    const allCards = Array.from(track.children);
    let currentIndex = realCount; // Start at first real set
    let isTransitioning = false;
    let autoplayTimer = null;

    function getCardWidth() {
      const card = allCards[0];
      if (!card) return 280;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.gap) || 20;
      return card.getBoundingClientRect().width + gap;
    }

    function updatePosition(animate = true) {
      const cardWidth = getCardWidth();
      if (animate) {
        isTransitioning = true;
        track.style.transition = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
      } else {
        track.style.transition = 'none';
      }
      track.style.transform = `translate3d(-${currentIndex * cardWidth}px, 0, 0)`;
    }

    updatePosition(false);

    track.addEventListener('transitionend', () => {
      isTransitioning = false;
      const cardWidth = getCardWidth();
      if (currentIndex >= realCount * 2) {
        currentIndex -= realCount;
        track.style.transition = 'none';
        track.style.transform = `translate3d(-${currentIndex * cardWidth}px, 0, 0)`;
      } else if (currentIndex < realCount) {
        currentIndex += realCount;
        track.style.transition = 'none';
        track.style.transform = `translate3d(-${currentIndex * cardWidth}px, 0, 0)`;
      }
    });

    function next() {
      if (isTransitioning) return;
      currentIndex++;
      updatePosition(true);
    }

    function prev() {
      if (isTransitioning) return;
      currentIndex--;
      updatePosition(true);
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        next();
        resetAutoplay();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        prev();
        resetAutoplay();
      });
    }

    // Touch Swipe Support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    trackWrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      currentX = startX;
      isDragging = true;
      stopAutoplay();
    }, { passive: true });

    trackWrapper.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });

    trackWrapper.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      const diffX = startX - currentX;
      if (Math.abs(diffX) > 30) {
        if (diffX > 0) next();
        else prev();
      }
      startAutoplay();
    }, { passive: true });

    function startAutoplay() {
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        next();
      }, 4500);
    }

    function stopAutoplay() {
      if (autoplayTimer) clearInterval(autoplayTimer);
    }

    function resetAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    sec.addEventListener('mouseenter', stopAutoplay);
    sec.addEventListener('mouseleave', startAutoplay);

    window.addEventListener('resize', () => {
      updatePosition(false);
    });

    startAutoplay();
  });
}

/* ==========================================================================
   AJAX CART DRAWER
   ========================================================================== */
function initCartDrawer() {
  const cartButtons = document.querySelectorAll('[data-action="open-cart"], .icon-btn[title="Cart"]');
  const cartOverlay = document.querySelector('.cart-drawer-overlay');
  const cartDrawer = document.querySelector('.cart-drawer');
  const closeBtn = document.querySelector('.cart-drawer-close');

  function openCart() {
    if (cartOverlay && cartDrawer) {
      cartOverlay.classList.add('active');
      cartDrawer.classList.add('active');
      document.body.classList.add('cart-open');
      refreshCartDrawer();
    }
  }

  function closeCart() {
    if (cartOverlay && cartDrawer) {
      cartOverlay.classList.remove('active');
      cartDrawer.classList.remove('active');
      document.body.classList.remove('cart-open');
    }
  }

  cartButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openCart();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCart();
  });

  // Global Add to Cart listener
  document.addEventListener('submit', (e) => {
    if (e.target && e.target.matches('form[action*="/cart/add"]')) {
      e.preventDefault();
      const formData = new FormData(e.target);
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(res => res.json())
      .then(item => {
        openCart();
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
      });
    }
  });
}

function refreshCartDrawer() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      updateCartBadge(cart.item_count);
      renderCartDrawerItems(cart);
    })
    .catch(err => console.error('Error fetching cart:', err));
}

function updateCartBadge(count) {
  const badges = document.querySelectorAll('.cart-badge');
  badges.forEach(b => {
    b.textContent = count || 0;
  });
}

function renderCartDrawerItems(cart) {
  const body = document.querySelector('.cart-drawer-body');
  const subtotalVal = document.querySelector('.drawer-subtotal-val');
  if (!body) return;

  if (subtotalVal) {
    subtotalVal.textContent = formatMoney(cart.total_price);
  }

  if (cart.item_count === 0) {
    body.innerHTML = `
      <div style="text-align: center; padding: 40px 0; color: var(--text-muted);">
        <i class="fa-solid fa-bag-shopping" style="font-size: 2.5rem; color: var(--gold-primary); margin-bottom: 14px;"></i>
        <p style="font-size: 0.95rem; font-weight: 600; color: #FFF; margin-bottom: 6px;">Your Cart is Empty</p>
        <p style="font-size: 0.8rem; margin-bottom: 20px;">Browse our rackets and badminton gear to get started.</p>
        <a href="/collections/all" class="btn-primary" style="padding: 10px 20px; font-size: 0.75rem;">CONTINUE SHOPPING</a>
      </div>
    `;
    return;
  }

  let html = '';
  cart.items.forEach(item => {
    const itemPrice = formatMoney(item.final_line_price);
    html += `
      <div class="drawer-item" data-key="${item.key}">
        <img src="${item.image || ''}" alt="${escapeHtml(item.product_title)}" class="drawer-item-img">
        <div class="drawer-item-details">
          <a href="${item.url}" class="drawer-item-title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="drawer-item-variant">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="drawer-item-price">${itemPrice}</div>
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 8px;">
            <div class="quantity-picker" style="height: 28px;">
              <button class="qty-btn" onclick="changeCartQty('${item.key}', ${item.quantity - 1})" style="width: 26px; height: 26px; font-size: 0.8rem;">-</button>
              <span style="font-size: 0.8rem; font-weight: 700; color: #FFF; padding: 0 8px;">${item.quantity}</span>
              <button class="qty-btn" onclick="changeCartQty('${item.key}', ${item.quantity + 1})" style="width: 26px; height: 26px; font-size: 0.8rem;">+</button>
            </div>
            <button class="drawer-item-remove" onclick="changeCartQty('${item.key}', 0)">
              <i class="fa-regular fa-trash-can"></i> Remove
            </button>
          </div>
        </div>
      </div>
    `;
  });

  body.innerHTML = html;
}

window.changeCartQty = function(key, qty) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({ id: key, quantity: qty })
  })
  .then(res => res.json())
  .then(cart => {
    refreshCartDrawer();
  })
  .catch(err => console.error('Error changing quantity:', err));
};

/* ==========================================================================
   SEARCH DRAWER / PREDICTIVE SEARCH
   ========================================================================== */
function initSearchDrawer() {
  const searchButtons = document.querySelectorAll('[data-action="open-search"], .icon-btn[title="Search"]');
  const searchOverlay = document.querySelector('.search-drawer-overlay');
  const searchInput = document.querySelector('.search-drawer-input');
  const searchResults = document.querySelector('.search-results-list');
  const closeBtn = document.querySelector('.search-drawer-close');

  function openSearch() {
    if (searchOverlay) {
      searchOverlay.classList.add('active');
      if (searchInput) searchInput.focus();
    }
  }

  function closeSearch() {
    if (searchOverlay) {
      searchOverlay.classList.remove('active');
    }
  }

  searchButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openSearch();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closeSearch);
  if (searchOverlay) {
    searchOverlay.addEventListener('click', (e) => {
      if (e.target === searchOverlay) closeSearch();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });

  if (searchInput) {
    let timeout = null;
    searchInput.addEventListener('input', () => {
      clearTimeout(timeout);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        if (searchResults) searchResults.innerHTML = '';
        return;
      }

      timeout = setTimeout(() => {
        fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`)
          .then(res => res.json())
          .then(data => {
            const products = data.resources?.results?.products || [];
            if (!searchResults) return;
            if (products.length === 0) {
              searchResults.innerHTML = '<div style="color: var(--text-muted); padding: 15px 0;">No products found matching your search.</div>';
              return;
            }

            let html = '';
            products.forEach(p => {
              html += `
                <a href="${p.url}" style="display: flex; align-items: center; gap: 14px; padding: 10px; border-bottom: 1px solid var(--border-color); text-decoration: none;">
                  <img src="${p.image || ''}" alt="${escapeHtml(p.title)}" style="width: 45px; height: 45px; object-fit: contain; background: #04070D; border-radius: 4px; padding: 4px;">
                  <div>
                    <div style="color: #FFF; font-size: 0.85rem; font-weight: 700;">${escapeHtml(p.title)}</div>
                    <div style="color: var(--gold-primary); font-size: 0.8rem; font-weight: 700;">${formatMoney(p.price * 100)}</div>
                  </div>
                </a>
              `;
            });
            searchResults.innerHTML = html;
          })
          .catch(err => console.error('Error fetching predictive search:', err));
      }, 300);
    });
  }
}

/* ==========================================================================
   MOBILE MENU DRAWER & OVERLAY
   ========================================================================== */
function initMobileMenu() {
  const toggleBtn = document.querySelector('.mobile-nav-toggle');
  const mobileDrawer = document.querySelector('.mobile-menu-drawer');
  const mobileOverlay = document.querySelector('.mobile-menu-overlay');
  const closeBtn = document.querySelector('.mobile-menu-close');

  function openMobileMenu() {
    if (mobileDrawer && mobileOverlay) {
      mobileDrawer.classList.add('active');
      mobileOverlay.classList.add('active');
      document.body.classList.add('menu-open');
    }
  }

  function closeMobileMenu() {
    if (mobileDrawer && mobileOverlay) {
      mobileDrawer.classList.remove('active');
      mobileOverlay.classList.remove('active');
      document.body.classList.remove('menu-open');
    }
  }

  if (toggleBtn) {
    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openMobileMenu();
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', closeMobileMenu);
  if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMobileMenu();
  });
}

/* ==========================================================================
   FAQ ACCORDIONS
   ========================================================================== */
function initFaqAccordions() {
  const faqQuestions = document.querySelectorAll('.faq-question');
  faqQuestions.forEach(q => {
    q.addEventListener('click', () => {
      const item = q.closest('.faq-item');
      if (item) {
        item.classList.toggle('active');
      }
    });
  });
}

/* ==========================================================================
   PRODUCT PAGE GALLERY & VARIANTS
   ========================================================================== */
function initProductPage() {
  const mainImg = document.querySelector('#ProductMainImage');
  const thumbs = document.querySelectorAll('.thumb-item');
  const galleryCount = document.querySelector('#GalleryCount');

  thumbs.forEach(t => {
    t.addEventListener('click', () => {
      thumbs.forEach(other => other.classList.remove('active'));
      t.classList.add('active');
      const newSrc = t.getAttribute('data-img-src');
      const idx = t.getAttribute('data-index');
      if (mainImg && newSrc) {
        mainImg.src = newSrc;
      }
      if (galleryCount && idx) {
        galleryCount.textContent = idx;
      }
    });
  });
}

/* ==========================================================================
   COLLECTION FILTERS, SORTING & MOBILE FILTER SHEET
   ========================================================================== */
function initCollectionFilters() {
  const filterInputs = document.querySelectorAll('.filter-checkbox');
  const sortSelect = document.querySelector('.shop-sort-select');
  const openFilterBtn = document.querySelector('.btn-open-mobile-filters');
  const filterSidebar = document.querySelector('.filter-sidebar');
  const filterOverlay = document.querySelector('.filter-sidebar-overlay');
  const closeFilterBtn = document.querySelector('.filter-sidebar-close');

  function openMobileFilters() {
    if (filterSidebar && filterOverlay) {
      filterSidebar.classList.add('active-mobile');
      filterOverlay.classList.add('active');
    }
  }

  function closeMobileFilters() {
    if (filterSidebar && filterOverlay) {
      filterSidebar.classList.remove('active-mobile');
      filterOverlay.classList.remove('active');
    }
  }

  if (openFilterBtn) openFilterBtn.addEventListener('click', openMobileFilters);
  if (closeFilterBtn) closeFilterBtn.addEventListener('click', closeMobileFilters);
  if (filterOverlay) filterOverlay.addEventListener('click', closeMobileFilters);

  filterInputs.forEach(input => {
    input.addEventListener('change', () => {
      const form = input.closest('form');
      if (form) form.submit();
    });
  });

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }
}

/* Helper Utilities */
function formatMoney(cents) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2);
  return '€' + value;
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>"']/g, function(m) {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[m];
  });
}

/* ==========================================================================
   CEDRA SPORTS - KALLES POWER FEATURES CONTROLLER
   ========================================================================== */
window.CedraTheme = window.CedraTheme || {};

CedraTheme.handleSwatchChange = function(input, productId) {
  const group = input.closest('.swatch-group');
  if (group) {
    const valueSpan = group.querySelector('.swatch-selected-value');
    if (valueSpan) valueSpan.textContent = input.value;

    group.querySelectorAll('.swatch-pill-label').forEach(lbl => lbl.classList.remove('active'));
    input.closest('.swatch-pill-label').classList.add('active');
  }

  // Collect selected option values
  const container = input.closest('.cedra-swatch-container');
  if (!container) return;

  const selectedOptions = [];
  container.querySelectorAll('.swatch-group').forEach(grp => {
    const checked = grp.querySelector('.swatch-input-hidden:checked');
    if (checked) selectedOptions.push(checked.value);
  });

  // Fetch product JSON if available or sync variant
  if (window.productVariants) {
    const matchingVariant = window.productVariants.find(v => {
      return v.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (matchingVariant) {
      const hiddenInput = document.querySelector('#SelectedVariantId');
      if (hiddenInput) hiddenInput.value = matchingVariant.id;

      const stickySelect = document.querySelector('#StickyVariantSelector');
      if (stickySelect) stickySelect.value = matchingVariant.id;

      const priceElem = document.querySelector('.product-price-current');
      if (priceElem) priceElem.textContent = formatMoney(matchingVariant.price);

      if (matchingVariant.featured_image) {
        const mainImg = document.querySelector('#ProductMainImage');
        if (mainImg) mainImg.src = matchingVariant.featured_image.src;
      }
    }
  }
};

CedraTheme.handleStickyVariantChange = function(select) {
  const hiddenInput = document.querySelector('#SelectedVariantId');
  if (hiddenInput) hiddenInput.value = select.value;
};

CedraTheme.openTab = function(evt, tabId) {
  const container = evt.target.closest('.product-tabs-container');
  if (!container) return;

  container.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
  container.querySelectorAll('.tab-content-panel').forEach(panel => panel.classList.remove('active'));

  evt.target.classList.add('active');
  const targetPanel = container.querySelector('#' + tabId);
  if (targetPanel) targetPanel.classList.add('active');
};

CedraTheme.setGridCols = function(btn, cols) {
  const grid = document.querySelector('#ShopProductGrid');
  if (!grid) return;

  document.querySelectorAll('.grid-view-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');

  grid.className = `product-grid shop-product-grid grid-cols-${cols}`;
};

CedraTheme.switchCardImage = function(dot, productId, colorValue) {
  const card = dot.closest('.product-card');
  if (!card) return;
  
  card.querySelectorAll('.card-swatch-dot').forEach(d => d.style.borderColor = 'rgba(255,255,255,0.4)');
  dot.style.borderColor = '#D4AF37';
};

// Helper Utilities for Quick View
if (typeof window.escapeHtml !== 'function') {
  window.escapeHtml = function(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };
}

if (typeof window.formatMoney !== 'function') {
  window.formatMoney = function(cents) {
    if (cents === null || cents === undefined) return '';
    var num = (typeof cents === 'number') ? cents : parseFloat(cents);
    if (isNaN(num)) return '';
    return '€' + (num / 100).toFixed(2);
  };
}

CedraTheme.closeQuickView = function() {
  const modal = document.querySelector('#CedraQuickViewModal');
  if (modal) {
    modal.style.display = 'none';
  }
  document.body.classList.remove('modal-open');
};

CedraTheme.changeQVQty = function(change) {
  const qtyInput = document.querySelector('#QVQtyInput');
  if (!qtyInput) return;
  let val = parseInt(qtyInput.value) || 1;
  val += change;
  if (val < 1) val = 1;
  qtyInput.value = val;
};

CedraTheme.switchQVImage = function(imgUrl, thumbEl) {
  const mainImg = document.querySelector('#QVMainImg');
  if (mainImg) {
    mainImg.src = imgUrl;
  }
  const thumbs = document.querySelectorAll('.qv-thumb-img');
  thumbs.forEach(t => t.classList.remove('active'));
  if (thumbEl) thumbEl.classList.add('active');
};

CedraTheme.openQuickView = function(handle) {
  let modal = document.querySelector('#CedraQuickViewModal');
  let body = document.querySelector('#QuickViewModalBody');
  
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'CedraQuickViewModal';
    modal.className = 'cedra-modal-backdrop';
    modal.setAttribute('onclick', 'if(event.target === this) CedraTheme.closeQuickView()');
    modal.innerHTML = `
      <div class="cedra-modal-content">
        <button type="button" class="modal-close-btn" onclick="CedraTheme.closeQuickView()">&times;</button>
        <div id="QuickViewModalBody"></div>
      </div>
    `;
    document.body.appendChild(modal);
    body = modal.querySelector('#QuickViewModalBody');
  }

  modal.style.display = 'flex';
  document.body.classList.add('modal-open');

  body.innerHTML = `
    <div style="text-align: center; padding: 40px;">
      <i class="fa-solid fa-spinner fa-spin fa-2x" style="color: #D4AF37;"></i>
      <p style="margin-top: 12px; color: #8E9BAE; font-size: 0.9rem;">Loading Cedra Product Details...</p>
    </div>
  `;

  fetch(`/products/${handle}.js`)
    .then(res => {
      if (!res.ok) throw new Error('Product fetch failed');
      return res.json();
    })
    .then(product => {
      window.currentQVProduct = product;
      const firstVariant = product.variants[0] || {};
      const isOnSale = product.compare_at_price > product.price;

      let imagesHtml = '';
      if (product.images && product.images.length > 1) {
        imagesHtml = `<div class="qv-thumbs-row">`;
        product.images.forEach((img, idx) => {
          imagesHtml += `
            <img src="${img}" class="qv-thumb-img ${idx === 0 ? 'active' : ''}" 
                 onclick="CedraTheme.switchQVImage('${img}', this)" alt="${escapeHtml(product.title)}">
          `;
        });
        imagesHtml += `</div>`;
      }

      let optionsHtml = '';
      if (product.options && product.options.length > 0 && product.variants.length > 1) {
        optionsHtml += `<div style="margin-bottom: 16px;">`;
        product.options.forEach((opt, idx) => {
          optionsHtml += `
            <div class="qv-option-group">
              <label class="qv-option-label">${escapeHtml(opt.name)}</label>
              <select class="qv-select qv-option-select" data-option-index="${idx}" onchange="CedraTheme.updateQVVariant()">
                ${opt.values.map(val => `<option value="${escapeHtml(val)}">${escapeHtml(val)}</option>`).join('')}
              </select>
            </div>
          `;
        });
        optionsHtml += `</div>`;
      }

      const cleanDesc = product.description 
        ? product.description.replace(/(<([^>]+)>)/gi, "").substring(0, 160) + '...'
        : 'Premium badminton equipment engineered for high performance and durability.';

      body.innerHTML = `
        <div class="qv-grid">
          <div class="qv-media-box">
            <img id="QVMainImg" src="${product.featured_image || product.images[0]}" alt="${escapeHtml(product.title)}" class="qv-main-img">
            ${imagesHtml}
          </div>
          <div>
            <span class="qv-vendor-tag">${escapeHtml(product.vendor || 'Cedra Sports')}</span>
            <h2 class="qv-title">${escapeHtml(product.title)}</h2>
            
            <div class="qv-price-row">
              <span id="QVPrice" class="qv-price">${formatMoney(product.price)}</span>
              ${isOnSale ? `<span id="QVComparePrice" class="qv-compare-price">${formatMoney(product.compare_at_price)}</span>` : ''}
              ${isOnSale ? `<span class="qv-sale-badge">SALE</span>` : ''}
            </div>

            <p class="qv-description">${escapeHtml(cleanDesc)}</p>

            ${optionsHtml}

            <input type="hidden" id="QVSelectedVariantId" value="${firstVariant.id}">

            <div class="qv-actions-row">
              <div class="qv-qty-box">
                <button type="button" class="qv-qty-btn" onclick="CedraTheme.changeQVQty(-1)">-</button>
                <input type="number" id="QVQtyInput" class="qv-qty-input" value="1" min="1" readonly>
                <button type="button" class="qv-qty-btn" onclick="CedraTheme.changeQVQty(1)">+</button>
              </div>

              <button type="button" id="QVAtcBtn" class="qv-atc-btn" 
                      onclick="CedraTheme.quickAddToCartFromQV()" 
                      ${!firstVariant.available ? 'disabled style="background: #64748B; cursor: not-allowed;"' : ''}>
                <i class="fa-solid fa-cart-shopping"></i> 
                <span id="QVAtcText">${firstVariant.available ? 'ADD TO CART' : 'SOLD OUT'}</span>
              </button>
            </div>

            <a href="/products/${product.handle}" class="qv-view-full-link">View Full Details &amp; Specifications &rarr;</a>
          </div>
        </div>
      `;
    })
    .catch(err => {
      console.error('Quick View Error:', err);
      body.innerHTML = `
        <div style="text-align: center; padding: 40px; color: #CBD5E1;">
          <i class="fa-solid fa-circle-exclamation fa-2x" style="color: #EF4444; margin-bottom: 12px;"></i>
          <p style="font-size: 1rem; font-weight: 600;">Unable to load Quick View details.</p>
          <a href="/products/${handle}" style="color: #D4AF37; display: inline-block; margin-top: 12px;">Go to product page &rarr;</a>
        </div>
      `;
    });
};

CedraTheme.updateQVVariant = function() {
  const product = window.currentQVProduct;
  if (!product || !product.variants) return;

  const selects = document.querySelectorAll('.qv-option-select');
  const selectedOptions = Array.from(selects).map(s => s.value);

  const matchedVariant = product.variants.find(variant => {
    return selectedOptions.every((optVal, idx) => variant.options[idx] === optVal);
  }) || product.variants[0];

  if (matchedVariant) {
    const varInput = document.querySelector('#QVSelectedVariantId');
    if (varInput) varInput.value = matchedVariant.id;

    const priceEl = document.querySelector('#QVPrice');
    if (priceEl) priceEl.textContent = formatMoney(matchedVariant.price);

    const compareEl = document.querySelector('#QVComparePrice');
    if (compareEl) {
      if (matchedVariant.compare_at_price > matchedVariant.price) {
        compareEl.textContent = formatMoney(matchedVariant.compare_at_price);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const atcBtn = document.querySelector('#QVAtcBtn');
    const atcText = document.querySelector('#QVAtcText');
    if (atcBtn && atcText) {
      if (matchedVariant.available) {
        atcBtn.disabled = false;
        atcBtn.style.background = '#1E9A60';
        atcBtn.style.cursor = 'pointer';
        atcText.textContent = 'ADD TO CART';
      } else {
        atcBtn.disabled = true;
        atcBtn.style.background = '#64748B';
        atcBtn.style.cursor = 'not-allowed';
        atcText.textContent = 'SOLD OUT';
      }
    }

    if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
      const mainImg = document.querySelector('#QVMainImg');
      if (mainImg) mainImg.src = matchedVariant.featured_image.src;
    }
  }
};

CedraTheme.quickAddToCartFromQV = function() {
  const varInput = document.querySelector('#QVSelectedVariantId');
  const qtyInput = document.querySelector('#QVQtyInput');
  const variantId = varInput ? parseInt(varInput.value) : null;
  const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

  if (!variantId) return;

  const atcBtn = document.querySelector('#QVAtcBtn');
  const atcText = document.querySelector('#QVAtcText');
  if (atcText) atcText.textContent = 'ADDING...';
  if (atcBtn) atcBtn.disabled = true;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
  })
  .then(res => res.json())
  .then(() => {
    CedraTheme.closeQuickView();
    if (typeof openCartDrawer === 'function') {
      openCartDrawer();
    } else if (typeof updateCartCount === 'function') {
      updateCartCount();
      window.location.href = '/cart';
    } else {
      window.location.href = '/cart';
    }
  })
  .catch(err => {
    console.error('Add to Cart Error:', err);
    if (atcText) atcText.textContent = 'ADD TO CART';
    if (atcBtn) atcBtn.disabled = false;
  });
};

CedraTheme.quickAddToCart = function(variantId) {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
  })
  .then(res => res.json())
  .then(() => {
    CedraTheme.closeQuickView();
    if (typeof openCartDrawer === 'function') openCartDrawer();
    else window.location.href = '/cart';
  });
};

CedraTheme.recalculateFBTPrice = function() {
  const box = document.querySelector('.cedra-fbt-box');
  if (!box) return;

  let totalCents = 0;
  box.querySelectorAll('.fbt-item').forEach(item => {
    const checkbox = item.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.checked) {
      const price = parseInt(item.getAttribute('data-price')) || 0;
      totalCents += price;
    }
  });

  const totalElem = document.querySelector('#FBTTotalPrice');
  if (totalElem) totalElem.textContent = formatMoney(totalCents);
};

CedraTheme.addFBTBundleToCart = function() {
  const box = document.querySelector('.cedra-fbt-box');
  if (!box) return;

  const itemsToAdd = [];
  box.querySelectorAll('.fbt-item').forEach(item => {
    const checkbox = item.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.checked) {
      const variantId = item.getAttribute('data-variant-id') || document.querySelector('#SelectedVariantId')?.value;
      if (variantId) {
        itemsToAdd.push({ id: parseInt(variantId), quantity: 1 });
      }
    }
  });

  if (itemsToAdd.length > 0) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: itemsToAdd })
    })
    .then(res => res.json())
    .then(() => {
      if (typeof openCartDrawer === 'function') openCartDrawer();
      else window.location.href = '/cart';
    });
  }
};

/* Init Sticky ATC Observer & Image Zoom */
document.addEventListener('DOMContentLoaded', () => {
  const stickyAtc = document.querySelector('#CedraStickyATC');
  const buyForm = document.querySelector('#ProductAddToCartForm');
  
  if (stickyAtc && buyForm) {
    window.addEventListener('scroll', () => {
      const formRect = buyForm.getBoundingClientRect();
      if (formRect.bottom < 0) {
        stickyAtc.classList.add('visible');
      } else {
        stickyAtc.classList.remove('visible');
      }
    });
  }

  // Magnifying Glass Zoom Effect
  initImageZoom();
});

function initImageZoom() {
  const wrapper = document.querySelector('#ZoomImageWrapper');
  const img = document.querySelector('#ProductMainImage');
  if (!wrapper || !img) return;

  wrapper.addEventListener('mousemove', (e) => {
    const rect = wrapper.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = 'scale(2.2)';
  });

  wrapper.addEventListener('mouseleave', () => {
    img.style.transform = 'scale(1)';
  });
}

CedraTheme.selectGalleryThumb = function(btn) {
  document.querySelectorAll('.product-gallery-thumbs .thumb-item').forEach(t => t.classList.remove('active'));
  btn.classList.add('active');

  const mainImg = document.querySelector('#ProductMainImage');
  const newSrc = btn.getAttribute('data-img-src');
  const newZoomSrc = btn.getAttribute('data-zoom-src');
  const idx = btn.getAttribute('data-index');
  const countElem = document.querySelector('#GalleryCount');

  if (mainImg && newSrc) {
    mainImg.src = newSrc;
    if (newZoomSrc) mainImg.setAttribute('data-zoom-src', newZoomSrc);
  }

  if (countElem && idx) {
    countElem.textContent = idx;
  }
};

CedraTheme.openLightboxModal = function() {
  const modal = document.querySelector('#CedraLightboxModal');
  const lightboxImg = document.querySelector('#LightboxMainImage');
  const mainImg = document.querySelector('#ProductMainImage');

  if (modal && lightboxImg && mainImg) {
    lightboxImg.src = mainImg.getAttribute('data-zoom-src') || mainImg.src;
    modal.style.display = 'flex';
  }
};

CedraTheme.closeLightboxModal = function() {
  const modal = document.querySelector('#CedraLightboxModal');
  if (modal) modal.style.display = 'none';
};

/* Product Tabs Switching Function */
CedraTheme.openTab = function(evt, tabName) {
  var i, tabcontent, tablinks;
  tabcontent = document.getElementsByClassName("tab-content-panel");
  for (i = 0; i < tabcontent.length; i++) {
    tabcontent[i].classList.remove("active");
  }
  tablinks = document.getElementsByClassName("tab-btn");
  for (i = 0; i < tablinks.length; i++) {
    tablinks[i].classList.remove("active");
  }
  var targetTab = document.getElementById(tabName);
  if (targetTab) {
    targetTab.classList.add("active");
  }
  if (evt && evt.currentTarget) {
    evt.currentTarget.classList.add("active");
  }
};

/* AJAX Cart Quantity & Item Removal Controller */
window.changeCartQty = function(lineKey, newQty) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: parseInt(newQty) })
  })
  .then(res => res.json())
  .then(cart => {
    // Dynamically update Cart Drawer & Page UI without page reload
    fetch(window.location.href)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newDrawerBody = doc.querySelector('.cart-drawer-body');
        const oldDrawerBody = document.querySelector('.cart-drawer-body');
        if (newDrawerBody && oldDrawerBody) oldDrawerBody.innerHTML = newDrawerBody.innerHTML;
        
        const newDrawerSub = doc.querySelector('.drawer-subtotal-val');
        const oldDrawerSub = document.querySelector('.drawer-subtotal-val');
        if (newDrawerSub && oldDrawerSub) oldDrawerSub.innerHTML = newDrawerSub.innerHTML;

        const newGoal = doc.querySelector('.cart-shipping-goal-box');
        const oldGoal = document.querySelector('.cart-shipping-goal-box');
        if (newGoal && oldGoal) oldGoal.innerHTML = newGoal.innerHTML;

        const newCartPage = doc.querySelector('.policy-box');
        const oldCartPage = document.querySelector('.policy-box');
        if (newCartPage && oldCartPage) oldCartPage.innerHTML = newCartPage.innerHTML;

        const newBadge = doc.querySelector('.cart-count-badge');
        const oldBadge = document.querySelector('.cart-count-badge');
        if (newBadge && oldBadge) oldBadge.innerHTML = newBadge.innerHTML;
      });
  })
  .catch(err => {
    console.error('Cart quantity update error:', err);
    window.location.reload();
  });
};

/* ==========================================================================
   COLLECTION DYNAMIC FILTERS & SORT ENGINE (MATCHING REFERENCE DESIGN)
   ========================================================================== */
function initCollectionFilters() {
  const filterForm = document.getElementById('CollectionFiltersForm');
  const productGrid = document.getElementById('ShopProductGrid');
  const showingCount = document.getElementById('ShopShowingCount');
  const sidebar = document.querySelector('.filter-sidebar');
  const overlay = document.querySelector('.filter-sidebar-overlay');
  const mobileBtn = document.querySelector('.btn-open-mobile-filters');
  const closeBtn = document.querySelector('.filter-sidebar-close');

  // Mobile Drawer Toggle
  if (mobileBtn && sidebar && overlay) {
    mobileBtn.addEventListener('click', () => {
      sidebar.classList.add('open');
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeMobileSidebar() {
    if (sidebar && overlay) {
      sidebar.classList.remove('open');
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  if (closeBtn) closeBtn.addEventListener('click', closeMobileSidebar);
  if (overlay) overlay.addEventListener('click', closeMobileSidebar);

  if (!productGrid) return;

  const productCards = Array.from(productGrid.querySelectorAll('.product-card'));
  if (productCards.length === 0) return;

  // Price Dual Slider Controls
  const minPriceInput = document.getElementById('FilterMinPrice');
  const maxPriceInput = document.getElementById('FilterMaxPrice');
  const minRangeInput = document.getElementById('RangeMinPrice');
  const maxRangeInput = document.getElementById('RangeMaxPrice');
  const sliderFill = document.getElementById('PriceSliderFill');
  const sliderMinLabel = document.getElementById('SliderMinLabel');
  const sliderMaxLabel = document.getElementById('SliderMaxLabel');

  function updatePriceSliderUI(minVal, maxVal, absMin = 0, absMax = 600) {
    if (!minRangeInput || !maxRangeInput) return;
    const minPercent = ((minVal - absMin) / (absMax - absMin)) * 100;
    const maxPercent = ((maxVal - absMin) / (absMax - absMin)) * 100;

    if (sliderFill) {
      sliderFill.style.left = `${Math.max(0, minPercent)}%`;
      sliderFill.style.right = `${Math.max(0, 100 - maxPercent)}%`;
    }
    if (sliderMinLabel) sliderMinLabel.textContent = `€${minVal}`;
    if (sliderMaxLabel) sliderMaxLabel.textContent = `€${maxVal}`;
  }

  // Range Inputs Listeners
  if (minRangeInput && maxRangeInput && minPriceInput && maxPriceInput) {
    minRangeInput.addEventListener('input', () => {
      let minVal = parseInt(minRangeInput.value);
      let maxVal = parseInt(maxRangeInput.value);

      if (minVal > maxVal - 5) {
        minVal = maxVal - 5;
        minRangeInput.value = minVal;
      }
      minPriceInput.value = `€${minVal}`;
      updatePriceSliderUI(minVal, maxVal, parseInt(minRangeInput.min), parseInt(maxRangeInput.max));
      filterProducts();
    });

    maxRangeInput.addEventListener('input', () => {
      let minVal = parseInt(minRangeInput.value);
      let maxVal = parseInt(maxRangeInput.value);

      if (maxVal < minVal + 5) {
        maxVal = minVal + 5;
        maxRangeInput.value = maxVal;
      }
      maxPriceInput.value = `€${maxVal}`;
      updatePriceSliderUI(minVal, maxVal, parseInt(minRangeInput.min), parseInt(maxRangeInput.max));
      filterProducts();
    });

    minPriceInput.addEventListener('change', () => {
      let rawVal = parseInt(minPriceInput.value.replace(/[^0-9]/g, '')) || 0;
      minRangeInput.value = rawVal;
      minRangeInput.dispatchEvent(new Event('input'));
    });

    maxPriceInput.addEventListener('change', () => {
      let rawVal = parseInt(maxPriceInput.value.replace(/[^0-9]/g, '')) || 600;
      maxRangeInput.value = rawVal;
      maxRangeInput.dispatchEvent(new Event('input'));
    });
  }

  // Category, Brand, Level Checkboxes & Sort Selects
  const categoryCheckboxes = Array.from(document.querySelectorAll('.filter-category-checkbox'));
  const brandCheckboxes = Array.from(document.querySelectorAll('.filter-brand-checkbox'));
  const levelCheckboxes = Array.from(document.querySelectorAll('.filter-level-checkbox'));
  const sidebarSortSelect = document.getElementById('SidebarSortBy');
  const topSortSelect = document.getElementById('SortBy');

  if (sidebarSortSelect && topSortSelect) {
    sidebarSortSelect.addEventListener('change', () => {
      topSortSelect.value = sidebarSortSelect.value;
      filterProducts();
    });
    topSortSelect.addEventListener('change', () => {
      sidebarSortSelect.value = topSortSelect.value;
      filterProducts();
    });
  }

  categoryCheckboxes.forEach(cb => cb.addEventListener('change', filterProducts));
  brandCheckboxes.forEach(cb => cb.addEventListener('change', filterProducts));
  levelCheckboxes.forEach(cb => cb.addEventListener('change', filterProducts));

  function filterProducts() {
    const checkedCategories = categoryCheckboxes
      .filter(cb => cb.checked)
      .map(cb => cb.value.toLowerCase());

    const checkedBrands = brandCheckboxes
      .filter(cb => cb.checked)
      .map(cb => cb.value.toLowerCase());

    const checkedLevels = levelCheckboxes
      .filter(cb => cb.checked)
      .map(cb => cb.value.toLowerCase());

    const currentMinPrice = minRangeInput ? parseFloat(minRangeInput.value) : 0;
    const currentMaxPrice = maxRangeInput ? parseFloat(maxRangeInput.value) : 999999;

    let visibleCount = 0;

    productCards.forEach(card => {
      const cardCategory = (card.getAttribute('data-category') || '').toLowerCase();
      const cardBrand = (card.getAttribute('data-brand') || '').toLowerCase();
      const cardLevel = (card.getAttribute('data-level') || '').toLowerCase();
      const cardPrice = parseFloat(card.getAttribute('data-price')) || 0;

      const matchesCategory = checkedCategories.length === 0 || 
        checkedCategories.some(cat => cardCategory.includes(cat) || cat.includes(cardCategory));

      const matchesBrand = checkedBrands.length === 0 || 
        checkedBrands.some(brand => cardBrand.includes(brand) || brand.includes(cardBrand));

      const matchesLevel = checkedLevels.length === 0 || 
        checkedLevels.some(lvl => cardLevel.includes(lvl) || lvl.includes(cardLevel));

      const matchesPrice = cardPrice >= currentMinPrice && cardPrice <= currentMaxPrice;

      if (matchesCategory && matchesBrand && matchesLevel && matchesPrice) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    const sortVal = topSortSelect ? topSortSelect.value : 'featured';
    sortProductCards(sortVal);

    if (showingCount) {
      showingCount.innerHTML = `SHOWING <strong>1–${visibleCount}</strong> OF <strong>${productCards.length}</strong> PRODUCTS`;
    }

    let noResultsMsg = productGrid.querySelector('.no-filter-results');
    if (visibleCount === 0) {
      if (!noResultsMsg) {
        noResultsMsg = document.createElement('div');
        noResultsMsg.className = 'no-filter-results';
        noResultsMsg.style.cssText = 'grid-column: 1 / -1; text-align: center; padding: 60px 0; color: #94A3B8;';
        noResultsMsg.innerHTML = `
          <i class="fa-solid fa-filter-circle-xmark" style="font-size: 2.5rem; color: #D4AF37; margin-bottom: 14px;"></i>
          <p style="font-size: 1rem; color: #FFF; font-weight: 700; margin-bottom: 6px;">No products match your filters</p>
          <p style="font-size: 0.85rem; margin-bottom: 16px;">Try adjusting your price range or clearing selected categories.</p>
          <button type="button" class="btn-outline-gold btn-reset-filters" style="padding: 8px 20px; font-size: 0.85rem; border-radius: 4px; cursor: pointer;">
            RESET FILTERS
          </button>
        `;
        productGrid.appendChild(noResultsMsg);
        noResultsMsg.querySelector('.btn-reset-filters').addEventListener('click', resetFilters);
      }
      noResultsMsg.style.display = '';
    } else if (noResultsMsg) {
      noResultsMsg.style.display = 'none';
    }
  }

  function sortProductCards(sortVal) {
    const parent = productGrid;
    const cards = Array.from(parent.querySelectorAll('.product-card:not(.no-filter-results)'));

    cards.sort((a, b) => {
      const priceA = parseFloat(a.getAttribute('data-price')) || 0;
      const priceB = parseFloat(b.getAttribute('data-price')) || 0;
      const titleA = (a.getAttribute('data-title') || '').toLowerCase();
      const titleB = (b.getAttribute('data-title') || '').toLowerCase();

      if (sortVal === 'price-ascending' || sortVal === 'price-low-high') {
        return priceA - priceB;
      } else if (sortVal === 'price-descending' || sortVal === 'price-high-low') {
        return priceB - priceA;
      } else if (sortVal === 'title-ascending' || sortVal === 'title-a-z') {
        return titleA.localeCompare(titleB);
      } else if (sortVal === 'title-descending' || sortVal === 'title-z-a') {
        return titleB.localeCompare(titleA);
      }
      return 0;
    });

    cards.forEach(card => parent.appendChild(card));
  }

  function resetFilters() {
    categoryCheckboxes.forEach(cb => cb.checked = false);
    brandCheckboxes.forEach(cb => cb.checked = false);
    levelCheckboxes.forEach(cb => cb.checked = false);
    if (minRangeInput && maxRangeInput) {
      minRangeInput.value = minRangeInput.min;
      maxRangeInput.value = maxRangeInput.max;
      if (minPriceInput) minPriceInput.value = `€${minRangeInput.min}`;
      if (maxPriceInput) maxPriceInput.value = `€${maxRangeInput.max}`;
      updatePriceSliderUI(parseInt(minRangeInput.min), parseInt(maxRangeInput.max), parseInt(minRangeInput.min), parseInt(maxRangeInput.max));
    }
    filterProducts();
  }

  if (minRangeInput && maxRangeInput) {
    updatePriceSliderUI(parseInt(minRangeInput.value), parseInt(maxRangeInput.value), parseInt(minRangeInput.min), parseInt(maxRangeInput.max));
  }
}



